import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './UseCasePages.css';
import purpleShieldImg from '../../assets/home/purpleshield.png';

const UseCaseLegal = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const sectionIds = ['uc-hero', 'section-overview', 'section-safety-score', 'section-document-writer', 'section-analysis', 'section-compliance', 'section-cta'];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + winHeight < docHeight - 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const title = el.querySelector('h2, p');
      const target = title || el;
      const nav = document.querySelector('.nav-container');
      const navH = nav ? nav.offsetHeight : 64;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - navH - 40, behavior: 'smooth' });
    }
  };

  const scrollToSection = useCallback((direction) => {
    const currentScroll = window.scrollY;
    const nav = document.querySelector('.nav-container');
    const navH = nav ? nav.offsetHeight : 64;
    const positions = sectionIds.map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const title = el.querySelector('h2, p');
      const target = title || el;
      return { scrollTo: target.getBoundingClientRect().top + window.scrollY - navH - 40 };
    }).filter(Boolean);

    if (direction === 'down') {
      const next = positions.find(p => p.scrollTo > currentScroll + 10);
      if (next) window.scrollTo({ top: next.scrollTo, behavior: 'smooth' });
    } else {
      const prev = [...positions].reverse().find(p => p.scrollTo < currentScroll - 10);
      if (prev) window.scrollTo({ top: prev.scrollTo, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <div className="scroll-float-wrap">
        {canScrollUp && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('up')} aria-label="Scroll up">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,6 4,16 20,16" fill="currentColor"/></svg>
          </button>
        )}
        {canScrollDown && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('down')} aria-label="Scroll down">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,18 4,8 20,8" fill="currentColor"/></svg>
          </button>
        )}
      </div>

      <section id="uc-hero" className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">Legal Services</h1>
      </section>

      <section className="ucp-content">
        <div className="ucp-layout">
          <h2 className="ucp-heading">Purple Team Testing for Legal AI</h2>
          <p className="ucp-description">
            Increase safety scores for legal analysis and document writer models using continuous purple team testing — combining offensive red team attacks with defensive blue team validation to ensure legal AI produces accurate, safe, and compliant outputs.
          </p>

          <div className="ucp-cta" style={{ marginTop: 0, marginBottom: 20 }}>
            <button className="ucp-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              Get a Live Demo &rarr;
            </button>
          </div>

          {/* 4 Feature Boxes */}
          <div className="ucp-features">
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">AI Safety Scoring</h3>
              </div>
              <p className="ucp-feature-desc">Quantify and improve safety scores for legal AI systems with continuous automated testing.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-safety-score')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">Document Writer Safety</h3>
              </div>
              <p className="ucp-feature-desc">Validate AI-generated legal documents against accuracy, bias, and hallucination benchmarks.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-document-writer')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">Legal Analysis Testing</h3>
              </div>
              <p className="ucp-feature-desc">Test legal research and analysis models for citation accuracy, reasoning quality, and bias.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-analysis')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">Regulatory Compliance</h3>
              </div>
              <p className="ucp-feature-desc">Ensure legal AI meets bar association guidelines, ethics rules, and client confidentiality standards.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-compliance')}>
                <span>&#8595;</span>
              </div>
            </div>
          </div>

          {/* Overview Section */}
          <div id="section-overview" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">The Challenge: AI in Legal Practice</h2>
            <p className="ucp-section-desc">
              Law firms and legal departments are adopting AI for contract analysis, legal research, document drafting, and case strategy. But legal AI introduces unique risks — hallucinated case citations, biased analysis, confidentiality breaches, and outputs that don't meet the standard of care. Without rigorous testing, a single AI-generated document with fabricated citations can lead to sanctions, malpractice claims, and client harm.
            </p>
            <div className="ucp-diagram">
              <div className="ucp-diagram-flow">
                <div className="ucp-diagram-node ucp-node-threat">
                  <div className="ucp-node-icon">&#128308;</div>
                  <span>Red Team Attacks</span>
                </div>
                <div className="ucp-diagram-arrow">&rarr;</div>
                <div className="ucp-diagram-node ucp-node-shield">
                  <img src={purpleShieldImg} alt="Bltz Purple Team" className="ucp-node-logo" />
                  <span>Bltz Purple Team</span>
                </div>
                <div className="ucp-diagram-arrow">&rarr;</div>
                <div className="ucp-diagram-node ucp-node-safe">
                  <div className="ucp-node-icon">&#9878;</div>
                  <span>Safer Legal AI</span>
                </div>
              </div>
            </div>
          </div>

          {/* Safety Score Section */}
          <div id="section-safety-score" className="ucp-section">
            <h2 className="ucp-section-title">Increasing Safety Scores with Purple Team</h2>
            <p className="ucp-section-desc">
              Bltz Purple Team continuously tests legal AI models by launching adversarial red team attacks — prompted hallucinations, bias injections, confidentiality extraction attempts — while the blue team validates defenses and measures safety improvements. Each testing cycle produces a quantified safety score, giving legal teams a measurable benchmark for AI reliability.
            </p>
            <div className="ucp-score-demo">
              <div className="ucp-score-before">
                <h4>Before Purple Team</h4>
                <div className="ucp-score-bar">
                  <div className="ucp-score-fill ucp-fill-low" style={{ width: '42%' }}></div>
                </div>
                <span className="ucp-score-value ucp-value-low">Safety Score: 42/100</span>
                <ul className="ucp-score-issues">
                  <li>Citation hallucination in 23% of outputs</li>
                  <li>Client data leakage via prompt manipulation</li>
                  <li>Biased language in contract generation</li>
                  <li>Inconsistent legal reasoning chains</li>
                </ul>
              </div>
              <div className="ucp-score-arrow">&#8594;</div>
              <div className="ucp-score-after">
                <h4>After Purple Team</h4>
                <div className="ucp-score-bar">
                  <div className="ucp-score-fill ucp-fill-high" style={{ width: '94%' }}></div>
                </div>
                <span className="ucp-score-value ucp-value-high">Safety Score: 94/100</span>
                <ul className="ucp-score-issues">
                  <li>Citation accuracy verified to 99.2%</li>
                  <li>Confidentiality guardrails validated</li>
                  <li>Bias detection and mitigation active</li>
                  <li>Reasoning quality benchmarked and improved</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Document Writer Section */}
          <div id="section-document-writer" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">Legal Document Writer Safety</h2>
            <p className="ucp-section-desc">
              AI document writers for contracts, briefs, and memoranda must produce outputs that meet professional standards. Bltz Purple Team tests document generation models against hundreds of adversarial scenarios — inserting contradictory clauses, testing for template injection, and validating consistency across jurisdictions.
            </p>
            <div className="ucp-checklist">
              <div className="ucp-check-item">
                <span className="ucp-check-icon">&#10003;</span>
                <div>
                  <h4>Hallucination Detection</h4>
                  <p>Red team prompts designed to trigger fabricated case law, statutes, and legal precedents — validated against actual legal databases.</p>
                </div>
              </div>
              <div className="ucp-check-item">
                <span className="ucp-check-icon">&#10003;</span>
                <div>
                  <h4>Template Injection Testing</h4>
                  <p>Adversarial inputs that attempt to override document templates, insert rogue clauses, or alter standard terms and conditions.</p>
                </div>
              </div>
              <div className="ucp-check-item">
                <span className="ucp-check-icon">&#10003;</span>
                <div>
                  <h4>Cross-Jurisdiction Validation</h4>
                  <p>Automated tests ensuring documents are jurisdiction-appropriate and don't mix legal standards across states or regions.</p>
                </div>
              </div>
              <div className="ucp-check-item">
                <span className="ucp-check-icon">&#10003;</span>
                <div>
                  <h4>Privilege &amp; Confidentiality</h4>
                  <p>Tests for attorney-client privilege leakage — ensuring AI never reveals protected communications in generated outputs.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Analysis Section */}
          <div id="section-analysis" className="ucp-section">
            <h2 className="ucp-section-title">Legal Analysis Model Testing</h2>
            <p className="ucp-section-desc">
              Legal research and case analysis models require rigorous validation to ensure accurate citation, sound reasoning, and unbiased conclusions. Bltz Purple Team runs continuous adversarial tests against analysis models, measuring citation accuracy, logical consistency, and bias indicators across thousands of legal scenarios.
            </p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Tested</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">Citation Accuracy</span>
                  <p>Red team generates prompts designed to elicit fabricated case citations. Blue team cross-references every citation against legal databases and flags hallucinations.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon ucp-result-pass">&#10003;</span>
                  <span>99.2% citation accuracy after 3 purple team cycles</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Tested</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">Reasoning Consistency</span>
                  <p>Adversarial prompts present contradictory legal arguments to test whether the model produces logically consistent analysis under pressure.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon ucp-result-pass">&#10003;</span>
                  <span>Reasoning quality improved 47% across test scenarios</span>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Section */}
          <div id="section-compliance" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">Legal &amp; Ethical Compliance</h2>
            <p className="ucp-section-desc">
              Legal AI must operate within the ethical frameworks established by bar associations, professional conduct rules, and client confidentiality requirements. Bltz Purple Team testing validates compliance across all dimensions.
            </p>
            <div className="ucp-compliance-grid">
              <div className="ucp-compliance-card">
                <h4>ABA Model Rules</h4>
                <p>Ensure AI-assisted legal work meets ABA Model Rules of Professional Conduct for competence and supervision.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>Attorney-Client Privilege</h4>
                <p>Validate that AI systems never inadvertently disclose privileged communications or work product.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>Duty of Competence</h4>
                <p>Test that AI outputs meet the standard of care expected from competent legal practitioners in each jurisdiction.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>Unauthorized Practice</h4>
                <p>Ensure AI clearly delineates between legal information and legal advice to prevent unauthorized practice of law.</p>
              </div>
            </div>
          </div>

          {/* Metrics Section */}
          <div className="ucp-section">
            <h2 className="ucp-section-title">Measurable Results</h2>
            <div className="ucp-metrics">
              <div className="ucp-metric">
                <span className="ucp-metric-value">94/100</span>
                <span className="ucp-metric-label">Avg. Safety Score After Testing</span>
              </div>
              <div className="ucp-metric">
                <span className="ucp-metric-value">99.2%</span>
                <span className="ucp-metric-label">Citation Accuracy</span>
              </div>
              <div className="ucp-metric">
                <span className="ucp-metric-value">500+</span>
                <span className="ucp-metric-label">Adversarial Scenarios Per Cycle</span>
              </div>
              <div className="ucp-metric">
                <span className="ucp-metric-value">0</span>
                <span className="ucp-metric-label">Privilege Leakage Incidents</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div id="section-cta" className="ucp-cta">
            <button className="ucp-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              See Legal AI Testing in Action &rarr;
            </button>
          </div>

          <span className="ucp-back-link" onClick={() => { navigate('/use-cases'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer' }}>
            ← Back to Use Cases
          </span>
        </div>
      </section>
    </>
  );
};

export default UseCaseLegal;
